// LMLIBREForm.js — LM libre – autres prestations (sans assurance), famille
// EXPERTISE / CONSEIL (demande d'Anthony du 20/10/2026).
// Même structure que les autres LM (entité, contact, cabinet, expert) ; la
// mission se saisit directement dans le texte de la lettre (maquette
// d'Anthony) : champs en ligne, listes de travaux / exclusions, et tableau
// d'honoraires libre (une ligne par diligence).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEtatGlobal } from "../../../EtatGlobal.js";
import { User, Building2, ChevronDown, Check, CalendarDays, Euro, Plus, X } from "lucide-react";
import { Input } from "../../../../components/ui/input.js";
import { Select } from "../../../../components/ui/select.js";
import { Textarea } from "../../../../components/ui/textArea.js";
import CabinetSelect from "../../components/CabinetSelect";
import SelectOuAutre from "../../components/SelectOuAutre";
import { FORME_SOCIALE_OPTIONS, CIVILITE_OPTIONS, ORGANISATION_COMPTABLE_OPTIONS } from "../../components/lmSelectOptions";
import { calculHonorairesLibre, lignesHonoraires, lignesTexte, montantEuro } from "./lmLibreDocument";

const rempli = (v) => v !== " " && v !== null && v !== undefined && String(v).trim() !== "";
const LABEL = "text-slate-600 mb-1 block text-xs";
const INPUT = "bg-white border-slate-200 h-9 text-sm";
const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function Section({ titre, icon: Icon, valide, ouvert, onToggle, children }) {
  return (
    <div className="border-2 border-slate-200 rounded-xl bg-white overflow-hidden" style={{ padding: "10px" }}>
      <div
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        className="p-4 hover:bg-slate-50 transition-colors rounded-lg mb-2 cursor-pointer"
        onClick={onToggle}
      >
        <div style={{ display: "flex", gap: "25px", justifyContent: "center", alignItems: "center" }}>
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
              valide ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
            }`}
          >
            {valide ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
          </div>
          <h3 className="font-semibold text-slate-800" style={{ margin: 0 }}>
            {titre}
          </h3>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${ouvert ? "rotate-180" : ""}`} />
      </div>
      {ouvert && <div className="p-6 pt-2 space-y-4">{children}</div>}
    </div>
  );
}

// Champ saisi directement dans le texte de la lettre : il s'insère dans la
// phrase et revient à la ligne comme du texte. Non contrôlé (contentEditable) :
// le texte n'est réécrit depuis formData que s'il change ailleurs (brouillon
// repris, réinitialisation) et que le champ n'a pas le focus.
function ChampEnLigne({ value, onChange, placeholder, bloc = false }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.textContent !== (value || "")) el.textContent = value || "";
  }, [value]);
  return (
    <span
      ref={ref}
      role="textbox"
      aria-label={placeholder}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      onInput={(e) => onChange(e.currentTarget.textContent)}
      onPaste={(e) => {
        // Texte brut uniquement (pas de mise en forme collée depuis Word).
        e.preventDefault();
        document.execCommand("insertText", false, e.clipboardData.getData("text/plain").replace(/\s*\n\s*/g, " "));
      }}
      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
      className={`${bloc ? "block w-full" : "inline"} rounded px-1.5 py-0.5 border border-dashed border-[#8B1D41]/30 bg-[#8B1D41]/[0.04] text-slate-900 outline-none focus:border-solid focus:border-[#8B1D41] focus:bg-white empty:before:content-[attr(data-placeholder)] empty:before:italic empty:before:text-[#8B1D41]/50`}
    />
  );
}

// Liste à puces modifiable (travaux, exclusions).
function ListeLignes({ lignes, placeholder, onChange, ajout }) {
  const maj = (i, v) => onChange(lignes.map((l, j) => (j === i ? v : l)));
  return (
    <div className="space-y-1.5 pl-2">
      {lignes.map((l, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="text-[#8B1D41] leading-7">•</span>
          <div className="flex-1 leading-7">
            <ChampEnLigne value={l} onChange={(v) => maj(i, v)} placeholder={placeholder} bloc />
          </div>
          {lignes.length > 1 && (
            <button
              type="button"
              onClick={() => onChange(lignes.filter((_, j) => j !== i))}
              className="mt-1.5 text-slate-400 hover:text-red-600"
              title="Retirer la ligne"
              aria-label="Retirer la ligne"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={() => onChange([...lignes, ""])} className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#8B1D41]">
        <Plus className="w-3.5 h-3.5" /> {ajout}
      </button>
    </div>
  );
}

const Coupure = () => (
  <div className="flex items-center gap-3 text-[11px] text-slate-300 select-none" aria-hidden="true">
    <span className="flex-1 border-t border-dashed border-slate-200" />
    […]
    <span className="flex-1 border-t border-dashed border-slate-200" />
  </div>
);
const SousTitre = ({ children }) => <p className="text-[11px] font-semibold tracking-wide uppercase text-slate-500 m-0">{children}</p>;

function LMLIBREForm({ handleInputChange, handleDateClotureChange, setDetailsMission, setDetailsHonoraire }) {
  const { formData, setFormData } = useEtatGlobal();
  const [ouverts, setOuverts] = useState({ entite: true, mission: true, honoraires: true });
  const basculer = (k) => setOuverts((o) => ({ ...o, [k]: !o[k] }));
  const changer = (champ) => (valeur) => setFormData((prev) => ({ ...prev, [champ]: valeur }));

  const travaux = useMemo(() => lignesTexte(formData.libre_travaux), [formData.libre_travaux]);
  const exclusions = useMemo(() => lignesTexte(formData.libre_exclusions), [formData.libre_exclusions]);
  const honoraires = useMemo(() => lignesHonoraires(formData.libre_honoraires), [formData.libre_honoraires]);
  const majHonoraires = (lignes) => setFormData((prev) => ({ ...prev, libre_honoraires: lignes }));
  const majLigneHono = (i, champ, v) => majHonoraires(honoraires.map((l, j) => (j === i ? { ...l, [champ]: v } : l)));

  // Totaux recalculés à chaque saisie et repris dans formData (liste des
  // brouillons ; le document les recalcule lui-même à la génération).
  const hono = useMemo(() => calculHonorairesLibre(formData), [formData]);
  useEffect(() => {
    const ht = montantEuro(hono.ht);
    const tva = montantEuro(hono.tva);
    const ttc = montantEuro(hono.ttc);
    if (formData.total_honoraires === ht && formData.TVA_honoraires === tva && formData.total_honoraires_ttc === ttc) return;
    setFormData((prev) => ({ ...prev, total_honoraires: ht, TVA_honoraires: tva, total_honoraires_ttc: ttc }));
  }, [hono, formData.total_honoraires, formData.TVA_honoraires, formData.total_honoraires_ttc, setFormData]);

  // Indicateurs de la jauge de progression (aucun champ bloquant ici).
  const entiteValide = ["nom_client", "lieu_collab", "nom", "prenom", "email", "expert_comptable_nom"].every((c) => rempli(formData[c]));
  const missionValide =
    ["nom_mission", "description_mission", "livrable", "descriptif_mission"].every((c) => rempli(formData[c])) &&
    travaux.some(rempli) &&
    rempli(formData.mois_intervention);
  const honorairesValide = hono.ht !== null;
  useEffect(() => {
    setDetailsMission(entiteValide && missionValide);
  }, [entiteValide, missionValide, setDetailsMission]);
  useEffect(() => {
    setDetailsHonoraire(honorairesValide);
  }, [honorairesValide, setDetailsHonoraire]);

  return (
    <div className="Container space-y-4">
      {/* 1. Votre entité */}
      <Section titre="1. Votre entité" icon={Building2} valide={entiteValide} ouvert={ouverts.entite} onToggle={() => basculer("entite")}>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Forme sociale</label>
                <SelectOuAutre name="forme_sociale" value={formData.forme_sociale} onChange={handleInputChange} options={FORME_SOCIALE_OPTIONS} />
              </div>
              <div>
                <label className={LABEL}>Nom du client</label>
                <Input type="text" name="nom_client" value={formData.nom_client} onChange={handleInputChange} className={INPUT} />
              </div>
            </div>
            <CabinetSelect />
            <div>
              <label className={LABEL}>Adresse</label>
              <Input type="text" name="adresse" value={formData.adresse} onChange={handleInputChange} className={INPUT} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={LABEL}>SIREN</label>
                <Input type="text" name="siren" value={formData.siren} maxLength="9" onChange={handleInputChange} className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Activité principale</label>
                <Input type="text" name="acticite_principale" value={formData.acticite_principale} onChange={handleInputChange} className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Date de clôture</label>
                <div style={{ display: "flex", width: "100%" }}>
                  <Select name="date_cloture_jour" value={formData.date_cloture_jour} onChange={handleDateClotureChange}>
                    {[...Array(31).keys()].map((d) => (
                      <option key={d} value={String(d + 1).padStart(2, "0")}>
                        {String(d + 1).padStart(2, "0")}
                      </option>
                    ))}
                  </Select>
                  <Select name="date_cloture_mois" value={formData.date_cloture_mois} onChange={handleDateClotureChange}>
                    {MOIS.map((m, i) => (
                      <option key={m} value={String(i + 1).padStart(2, "0")}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div>
              <label className={LABEL}>Organisation comptable</label>
              <SelectOuAutre
                name="organisation_comptable"
                value={formData.organisation_comptable}
                onChange={handleInputChange}
                options={ORGANISATION_COMPTABLE_OPTIONS}
              />
            </div>
            <div>
              <label className={LABEL}>Autres informations utiles (facultatif)</label>
              <Textarea
                name="autres_informations"
                value={formData.autres_informations || ""}
                onChange={handleInputChange}
                rows={2}
                className="text-sm"
                placeholder="N'apparaît dans la lettre que si elle est renseignée"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={LABEL}>Expert-Comptable</label>
                <Select name="expert_comptable_nom" value={formData.expert_comptable_nom} onChange={handleInputChange}>
                  <option value="Samuel HOARAU">Samuel HOARAU</option>
                  <option value="Anthony HOARAU">Anthony HOARAU</option>
                </Select>
              </div>
              <div>
                <label className={LABEL}>Signataire</label>
                <Select name="expert_comptable" value={formData.expert_comptable} onChange={handleInputChange}>
                  <option value="Samuel HOARAU">Mrs Samuel HOARAU</option>
                  <option value="Anthony HOARAU">Mrs Anthony HOARAU</option>
                </Select>
              </div>
              <div>
                <label className={LABEL}>Mois d'intervention</label>
                <Input
                  type="text"
                  name="mois_intervention"
                  value={formData.mois_intervention}
                  onChange={handleInputChange}
                  placeholder="ex. novembre 2026"
                  className={INPUT}
                />
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 self-start">
            <h4 className="font-semibold text-slate-700 mb-5 text-sm flex items-center gap-2">
              <User className="w-4 h-4 text-[#8B1D41]" />
              Contact Référent
            </h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Civilité</label>
                  <SelectOuAutre name="civilite_lm" value={formData.civilite_lm} onChange={handleInputChange} options={CIVILITE_OPTIONS} />
                </div>
                <div>
                  <label className={LABEL}>Nom</label>
                  <Input type="text" name="nom" value={formData.nom} onChange={handleInputChange} className={INPUT} />
                </div>
              </div>
              <div>
                <label className={LABEL}>Prénom</label>
                <Input type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Email</label>
                <Input type="email" name="email" value={formData.email} onChange={handleInputChange} className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Téléphone</label>
                <Input type="text" name="telephone" value={formData.telephone} onChange={handleInputChange} className={INPUT} />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Notre mission — saisie dans le texte de la lettre */}
      <Section titre="2. Notre mission" icon={CalendarDays} valide={missionValide} ouvert={ouverts.mission} onToggle={() => basculer("mission")}>
        <div>
          <label className={LABEL}>Nom de la mission</label>
          <Input
            type="text"
            name="nom_mission"
            value={formData.nom_mission || ""}
            onChange={handleInputChange}
            placeholder="Saisir le nom de la mission"
            className={INPUT}
          />
          <p className="text-[11px] text-slate-400 mt-1 mb-0">
            Titre de la page de garde, et nom du document : « LM {rempli(formData.nom_mission) ? formData.nom_mission.trim() : "[nom de la mission]"} ».
          </p>
        </div>

        <div className="space-y-4 text-sm leading-7 text-slate-700 max-w-3xl">
          <Coupure />
          <p className="m-0">
            Nous vous remercions de la confiance que vous nous avez témoignée en envisageant de nous confier en qualité d'expert-comptable une
            mission <ChampEnLigne value={formData.description_mission} onChange={changer("description_mission")} placeholder="description" />.
          </p>
          <Coupure />
          <div className="space-y-1">
            <SousTitre>2.1. Durée de la mission</SousTitre>
            <p className="m-0">
              Notre mission est conclue ponctuellement. Elle débute à compter de votre acceptation, et prend fin à la remise de{" "}
              <ChampEnLigne value={formData.livrable} onChange={changer("livrable")} placeholder="livrable" />.
            </p>
          </div>
          <div className="space-y-1">
            <SousTitre>2.2. Nature et objectif de la mission</SousTitre>
            <p className="m-0">
              Notre mission consistera à{" "}
              <ChampEnLigne value={formData.descriptif_mission} onChange={changer("descriptif_mission")} placeholder="descriptif de la mission" />.
            </p>
          </div>
          <Coupure />
          <div className="space-y-2">
            <SousTitre>2.3. Méthodologie de la mission</SousTitre>
            <p className="m-0">Nos travaux consisteront en :</p>
            <ListeLignes lignes={travaux} placeholder="Travail réalisé" ajout="Ajouter une ligne" onChange={changer("libre_travaux")} />
            <p className="m-0 pt-1">Nos travaux ne consisteront pas en :</p>
            <ListeLignes
              lignes={exclusions}
              placeholder="Exclusion précise et adaptée à la mission"
              ajout="Ajouter une ligne"
              onChange={changer("libre_exclusions")}
            />
          </div>
          <Coupure />
        </div>
      </Section>

      {/* 3. Honoraires — une ligne par diligence */}
      <Section titre="3. Honoraires" icon={Euro} valide={honorairesValide} ouvert={ouverts.honoraires} onToggle={() => basculer("honoraires")}>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left font-semibold px-3 py-2">Diligences</th>
                <th className="text-right font-semibold px-3 py-2 w-44">Budget hors taxes (€)</th>
                <th className="w-10" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {honoraires.map((l, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">
                    <Textarea
                      value={l.libelle}
                      onChange={(e) => majLigneHono(i, "libelle", e.target.value)}
                      rows={1}
                      placeholder="Diligence"
                      aria-label="Diligence"
                      className="text-sm min-h-9"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={l.montant}
                      onChange={(e) => majLigneHono(i, "montant", e.target.value)}
                      placeholder="0,00"
                      aria-label="Budget hors taxes"
                      className={`${INPUT} text-right`}
                    />
                  </td>
                  <td className="pr-2 align-top pt-4 text-center">
                    {honoraires.length > 1 && (
                      <button
                        type="button"
                        onClick={() => majHonoraires(honoraires.filter((_, j) => j !== i))}
                        className="text-slate-400 hover:text-red-600"
                        title="Retirer la ligne"
                        aria-label="Retirer la ligne"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => majHonoraires([...honoraires, { libelle: "", montant: "" }])}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#8B1D41]"
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter une diligence
        </button>

        <div className="grid md:grid-cols-4 gap-4 items-end">
          <div>
            <label className={LABEL}>TVA (%)</label>
            <Input
              type="text"
              inputMode="decimal"
              name="TVA_honoraires_pourcentage"
              value={formData.TVA_honoraires_pourcentage || ""}
              onChange={handleInputChange}
              className={INPUT}
            />
          </div>
          <div className="md:col-span-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm grid grid-cols-3 gap-3">
            <div>
              <p className="text-[11px] text-slate-500 m-0">Total HT</p>
              <p className="font-semibold text-slate-800 m-0">{hono.ht === null ? "—" : montantEuro(hono.ht)}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 m-0">TVA</p>
              <p className="font-semibold text-slate-800 m-0">{hono.tva === null ? "—" : montantEuro(hono.tva)}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 m-0">Total TTC</p>
              <p className="font-semibold text-slate-800 m-0">{hono.ttc === null ? "—" : montantEuro(hono.ttc)}</p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export default LMLIBREForm;
